import { useApi } from 'api/api-context';
import axios from 'axios';
import Qs from 'qs';
import { FileManagerContext } from 'api/file-manager/file-manager-context';
import { useMemo } from 'react';
import { FILE_MANAGER_TIMEOUT, FILE_MANAGER_URL, FILE_UPLOAD_MAX_SIZE } from 'env';
import formatFileSize from 'utils/format-file-size';

/**
 * File Manager Provider.
 */
export default function FileManagerProvider({ children }: { children: React.ReactNode }) {
  const { jwt } = useApi();

  /**
   * The connector object = axios instance.
   */
  const value = useMemo(() => {
    const connector = axios.create({
      timeout: FILE_MANAGER_TIMEOUT,
      baseURL: FILE_MANAGER_URL,
    });

    connector.interceptors.request.use((cfg) => {
      // Add JWT to the request.
      if (jwt && !cfg.headers.Authorization) {
        cfg.headers.Authorization = `Bearer ${jwt}`;
      }

      // Serialize deep objects.
      cfg.paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: 'brackets', encode: false });

      return cfg;
    });

    /** Uploads the file and returns its ID. */
    const uploadFile = async ({
      fileName,
      contents,
    }: {
      fileName: string;
      contents: string | ArrayBuffer | null;
    }): Promise<string> => {
      try {
        const { data } = await connector.post('/file/upload', { fileName, contents });
        return data.fileId;
      } catch (e: any) {
        if (e?.response?.status === 413) {
          const formattedMaxFileSize = formatFileSize(FILE_UPLOAD_MAX_SIZE, { maxDecimals: 0 });
          throw new Error(`Súbor je príliš veľký. Maximálna veľkosť súboru je ${formattedMaxFileSize}`);
        }

        throw e;
      }
    };

    /** Retrieves the contents of the file. */
    const readFile = async ({ fileId }: { fileId: string }): Promise<Blob> => {
      const { data } = await connector.get(`/file/${fileId}`, { responseType: 'blob' });
      return data;
    };

    /** Retrieves the metadata of the file. */
    const getFileMetadata = async ({ fileId }: { fileId: string }) => {
      const { data } = await connector.get(`/file/${fileId}/metadata`);
      return data;
    };

    /** Retrieves the thumbnail of the file. */
    const getFileThumbnail = async ({ fileId, size }: { fileId: string; size: string }) => {
      const { data } = await connector.get(`/file/${fileId}/thumbnail/${size}`, { responseType: 'blob' });
      return data;
    };

    /** Creates a short-lived reusable access token for the file. */
    const createTimeAccessToken = async ({ fileId, validForMinutes }: { fileId: string; validForMinutes: number }) => {
      const { data } = await connector.post(`/file/${fileId}/create-time-access-token`, { validForMinutes });
      return data.token;
    };

    /** Builds a timed access URL for reading a file by token. */
    const buildTimeAccessFileUrl = ({ token, fileName }: { token: string; fileName: string }) =>
      `${window.location.origin}${FILE_MANAGER_URL}/time/file/${token}/${encodeURIComponent(fileName)}`;

    /** Builds a timed access URL for reading a thumbnail by token. */
    const buildTimeAccessThumbnailUrl = ({
      token,
      fileName,
      thumbnailSize,
    }: {
      token: string;
      fileName: string;
      thumbnailSize: string;
    }) =>
      `${window.location.origin}${FILE_MANAGER_URL}/time/file/${token}/${encodeURIComponent(
        fileName
      )}?thumbnailSize=${encodeURIComponent(thumbnailSize)}`;

    /** Initializes a large file upload. Returns the ID of the file. */
    const initLargeFile = async ({ fileName }: { fileName: string }) => {
      const { data } = await connector.post('/file/large/init', { fileName });
      return data.fileId;
    };

    /** Uploads a chunk of the large file. */
    const uploadLargeFile = async ({ fileId, contents }: { fileId: string; contents: string | ArrayBuffer | null }) => {
      await connector.post(`/file/large/${fileId}/upload`, { contents });
    };

    /** Finalizes the large file upload. */
    const finalizeLargeFile = async ({ fileId }: { fileId: string }) => {
      await connector.post(`/file/large/${fileId}/finalize`);
    };

    /** Creates an empty ZIP archive and returns its file ID. */
    const createEmptyZip = async ({ fileName }: { fileName: string }) => {
      const { data } = await connector.post('/file/zip/create-empty', { fileName });
      return data.fileId;
    };

    /** Adds files to an existing open ZIP archive. */
    const addZipFiles = async ({ fileId, files }: { fileId: string; files: { fileId: string; path: string }[] }) => {
      await connector.post(`/file/zip/${fileId}/add-files`, { files });
    };

    /** Finalizes an existing open ZIP archive. */
    const finalizeZip = async ({ fileId }: { fileId: string }) => {
      await connector.post(`/file/zip/${fileId}/finalize`, {});
    };

    /**
     * Creates a ZIP archive using the multi-step workflow:
     * create-empty -> add-files in batches -> finalize.
     *
     * onProgress is called once after each discrete step completes, enabling
     * the caller to drive a progress indicator.
     */
    const buildZip = async ({
      fileName,
      files,
      batchSize = files.length,
      onProgress,
    }: {
      fileName: string;
      files: { fileId: string; path: string }[];
      batchSize?: number;
      onProgress?: () => void;
    }): Promise<string> => {
      const { data: createData } = await connector.post('/file/zip/create-empty', { fileName });
      const zipFileId: string = createData.fileId;
      onProgress?.();

      const safeBatchSize = Math.max(1, batchSize);

      for (let i = 0; i < files.length; i += safeBatchSize) {
        await connector.post(`/file/zip/${zipFileId}/add-files`, { files: files.slice(i, i + safeBatchSize) });
        onProgress?.();
      }

      await connector.post(`/file/zip/${zipFileId}/finalize`, {});
      onProgress?.();

      return zipFileId;
    };

    /** Concatenates multiple PDF files into a single PDF. */
    const concatPdf = async ({ fileName, fileIds }: { fileName: string; fileIds: string[] }) => {
      const { data } = await connector.post('/file/pdf/concat', { fileName, fileIds });
      return data.fileId;
    };

    return {
      uploadFile,
      readFile,
      getFileMetadata,
      getFileThumbnail,
      createTimeAccessToken,
      buildTimeAccessFileUrl,
      buildTimeAccessThumbnailUrl,
      initLargeFile,
      uploadLargeFile,
      finalizeLargeFile,
      createEmptyZip,
      addZipFiles,
      finalizeZip,
      buildZip,
      concatPdf,
    };
  }, [jwt]);

  return <FileManagerContext.Provider value={value}>{children}</FileManagerContext.Provider>;
}
